import type { CsvPreview } from "../types";
import {
  END_DATE_HEADER_LC,
  START_DATE_HEADER_LC,
  resolveDateColumnHeader,
} from "../utils/dateColumns";
import type { MappedRow } from "../utils/mappedRows";

export interface PayComponentRowDraft {
  employeeCode: string;
  employeeName: string;
  component: string;
  startDate: string;
  endDate: string;
  /** Raw CSV cell values keyed by header. */
  columnValues: Record<string, string>;
}

/** Optional mapped date-column overrides (wizard session). */
export interface PayComponentRowDateColumns {
  startDateColumn?: string | null;
  endDateColumn?: string | null;
}

/** Quote CSV cells for re-import — do not alter leading `-`/`+`/`=` (amounts). */
function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function createPayComponentRowDraft(
  mappedRow: MappedRow,
  sourceRow: Record<string, string>,
  headers: readonly string[],
): PayComponentRowDraft {
  const columnValues: Record<string, string> = {};
  for (const header of headers) {
    columnValues[header] = sourceRow[header] ?? "";
  }
  return {
    employeeCode: mappedRow.employeeCode,
    employeeName: mappedRow.employeeName,
    component: mappedRow.component,
    startDate: mappedRow.startDate ?? "",
    endDate: mappedRow.endDate ?? "",
    columnValues,
  };
}

/**
 * Builds a MappedRow-shaped identity from a raw CSV preview row so the Edit
 * modal can open on Upload and map before validation has run.
 */
export function mappedRowFromPreviewSource(
  rowIndex: number,
  sourceRow: Record<string, string>,
  headers: readonly string[],
  componentColumn: string,
  dateColumns: PayComponentRowDateColumns = {},
): MappedRow {
  const headerList = [...headers];
  const employeeCodeHeader =
    resolveHeader(headers, ["Employee Code"]) ?? "Employee Code";
  const employeeNameHeader =
    resolveHeader(headers, ["Employee Name"]) ?? "Employee Name";
  const startDateHeader = resolveDateColumnHeader(
    headerList,
    dateColumns.startDateColumn,
    START_DATE_HEADER_LC,
  );
  const endDateHeader = resolveDateColumnHeader(
    headerList,
    dateColumns.endDateColumn,
    END_DATE_HEADER_LC,
  );
  const startDate = startDateHeader
    ? (sourceRow[startDateHeader] ?? "").trim()
    : "";
  const endDate = endDateHeader
    ? (sourceRow[endDateHeader] ?? "").trim()
    : "";
  return {
    index: rowIndex,
    employeeCode: sourceRow[employeeCodeHeader] ?? "",
    employeeName: sourceRow[employeeNameHeader] ?? "",
    component: componentColumn
      ? (sourceRow[componentColumn] ?? "").trim()
      : "",
    ...(startDate !== "" ? { startDate } : {}),
    ...(endDate !== "" ? { endDate } : {}),
    values: [],
  };
}

export function isPayComponentRowDraftDirty(
  original: PayComponentRowDraft,
  draft: PayComponentRowDraft,
): boolean {
  if (original.employeeCode !== draft.employeeCode) return true;
  if (original.employeeName !== draft.employeeName) return true;
  if (original.component !== draft.component) return true;
  if (original.startDate !== draft.startDate) return true;
  if (original.endDate !== draft.endDate) return true;
  for (const [key, value] of Object.entries(original.columnValues)) {
    if ((draft.columnValues[key] ?? "") !== value) return true;
  }
  for (const [key, value] of Object.entries(draft.columnValues)) {
    if ((original.columnValues[key] ?? "") !== value) return true;
  }
  return false;
}

function resolveHeader(
  headers: readonly string[],
  candidates: string[],
): string | null {
  const lower = new Map(headers.map((h) => [h.toLowerCase(), h]));
  for (const candidate of candidates) {
    const match = lower.get(candidate.toLowerCase());
    if (match) return match;
  }
  return null;
}

export function applyPayComponentRowDraftToPreview(
  preview: CsvPreview,
  rowIndex: number,
  draft: PayComponentRowDraft,
  componentColumn: string,
  dateColumns: PayComponentRowDateColumns = {},
): CsvPreview {
  const employeeCodeHeader = resolveHeader(preview.headers, ["Employee Code"]);
  const employeeNameHeader = resolveHeader(preview.headers, ["Employee Name"]);
  const startDateHeader = resolveDateColumnHeader(
    preview.headers,
    dateColumns.startDateColumn,
    START_DATE_HEADER_LC,
  );
  const endDateHeader = resolveDateColumnHeader(
    preview.headers,
    dateColumns.endDateColumn,
    END_DATE_HEADER_LC,
  );

  const nextRows = preview.rows.map((row, index) => {
    if (index !== rowIndex - 1) return row;
    const next: Record<string, string> = { ...row, ...draft.columnValues };
    // Only write identity fields when the CSV actually has those headers —
    // otherwise serializePreviewToCsvText would drop orphan keys.
    if (employeeCodeHeader) {
      next[employeeCodeHeader] = draft.employeeCode;
    }
    if (employeeNameHeader) {
      next[employeeNameHeader] = draft.employeeName;
    }
    if (startDateHeader) {
      next[startDateHeader] = draft.startDate;
    }
    if (endDateHeader) {
      next[endDateHeader] = draft.endDate;
    }
    if (componentColumn) {
      next[componentColumn] = draft.component;
    }
    return next;
  });

  return { ...preview, rows: nextRows };
}

export function serializePreviewToCsvText(preview: CsvPreview): string {
  const headerLine = preview.headers.map(escapeCsvCell).join(",");
  const body = preview.rows.map((row) =>
    preview.headers.map((header) => escapeCsvCell(row[header] ?? "")).join(","),
  );
  return [headerLine, ...body].join("\n");
}
